import { Badge, Button, Card, CardHeader, Skeleton } from "@helmr/ui";
import { ENTITY_MAP, checkMapping, entitiesForSheet, type FieldRef, type Mapping } from "@helmr/importer";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { saveMapping, suggestMapping, type ImportFile, type ImportJob } from "../../api/imports";
import { useServices } from "../../app/services";
import s from "../settings/settings.module.css";
import o from "./onboarding.module.css";
import { StepError, useImportScope } from "./shared";
import { t } from "./strings";

const ref = (r: FieldRef | null) => (r ? `${r.entity}.${r.field}` : "");
const parse = (v: string): FieldRef | null => (v ? { entity: v.slice(0, v.indexOf(".")) as FieldRef["entity"], field: v.slice(v.indexOf(".") + 1) } : null);
const label = (r: FieldRef) => ENTITY_MAP[r.entity].fields.find((f) => f.name === r.field)?.label ?? r.field;

/** Step 2: one card per sheet. Suggestions are only suggestions: nothing is saved until the person presses Save. */
export function MappingStep({ job, onReload, onDone, onAddSheet }: { job: ImportJob; onReload: () => void; onDone: () => void; onAddSheet: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  // Mapped sheets are collapsed (the API does not return a saved mapping); the first unmapped sheet opens by itself.
  const first = job.files.find((f) => !job.mappedFileIds.includes(f.id))?.id ?? null;
  const showing = open ?? first;
  return (
    <div className={s.page}>
      <Card aria-label={t.steps.mapping.title}>
        <CardHeader title={t.steps.mapping.title} subtitle={t.steps.mapping.body} />
        <div className={s.acts} style={{ padding: "0 16px 12px" }}>
          <Button size="sm" onClick={onAddSheet}>Add another sheet</Button>
          {first === null ? <span className={s.hint}>{t.mappedAll}</span> : null}
        </div>
      </Card>
      {job.files.map((f) => (
        <SheetMapper key={f.id} job={job} file={f} expanded={showing === f.id} mapped={job.mappedFileIds.includes(f.id)} onOpen={() => setOpen(f.id)} onReload={onReload} onSaved={onDone} />
      ))}
    </div>
  );
}

function SheetMapper({ job, file, expanded, mapped, onOpen, onReload, onSaved }: { job: ImportJob; file: ImportFile; expanded: boolean; mapped: boolean; onOpen: () => void; onReload: () => void; onSaved: () => void }) {
  const { client } = useServices();
  const sc = useImportScope(job.id);
  const q = useQuery({ queryKey: sc.key("imports", "suggest", job.id, file.id, file.sha256), queryFn: () => suggestMapping(client, job.id, file.id), enabled: expanded, staleTime: Infinity, retry: false });
  const [edit, setEdit] = useState<Mapping | null>(null);
  const suggested = q.data?.find((x) => x.fileId === file.id)?.mapping ?? null;
  useEffect(() => { if (suggested) setEdit(suggested); }, [suggested]);
  const save = useMutation({
    mutationFn: () => saveMapping(client, job.id, file.id, edit!),
    onSuccess: (r) => { sc.putJob(r.job); onSaved(); },
  });

  const check = edit ? checkMapping(edit) : null;
  const dup = new Set((check?.duplicateTargets ?? []).map(ref));
  const targets = entitiesForSheet(file.entity).flatMap((e) => ENTITY_MAP[e].fields.map((f) => ({ entity: e, field: f.name, label: f.label, required: f.required })));
  const set = (i: number, v: string) => setEdit((m) => (m ? { ...m, columns: m.columns.map((c, k) => (k === i ? { ...c, target: parse(v), confidence: v ? 1 : 0, reason: v ? "Chosen by you" : "" } : c)) } : m));

  return (
    <Card aria-label={`Columns of ${file.filename}`}>
      <CardHeader title={file.filename} subtitle={`${t.entities[file.entity]} · ${t.rows(file.rowCount)}`} action={mapped ? <Badge tone="ok">Matched</Badge> : <Badge tone="warn">Needs matching</Badge>} />
      {!expanded ? <div className={s.acts} style={{ padding: "0 16px 14px" }}><Button size="sm" onClick={onOpen}>{mapped ? "Edit columns" : "Match columns"}</Button></div> : null}
      {expanded && q.isPending ? <div className={s.skel} aria-busy="true" aria-label="Loading suggestions"><Skeleton height={34} /><Skeleton height={34} /><Skeleton height={34} /></div> : null}
      {expanded && q.error ? <div style={{ padding: "0 16px 14px" }}><StepError error={q.error} onReload={() => void q.refetch()} /></div> : null}
      {expanded && edit && check ? (
        <>
          <div className={o.mapRow}><span className={o.mapHead}>Column in your sheet</span><span className={o.mapHead}>Goes into Helmr as</span></div>
          {edit.columns.map((c, i) => {
            const id = `map-${file.id}-${i}`;
            const isDup = c.target !== null && dup.has(ref(c.target));
            return (
              <div key={c.sourceIndex} className={o.mapRow} data-bad={isDup}>
                <span className={o.src}><b>{c.header || `Column ${c.sourceIndex + 1}`}</b><small>{c.target ? (c.confidence >= 0.7 ? c.reason : `Check this: ${c.reason}`) : "Not imported"}</small></span>
                <select id={id} className={s.select} aria-label={`Maps ${c.header || `column ${c.sourceIndex + 1}`} to`} aria-invalid={isDup || undefined} value={ref(c.target)} onChange={(e) => set(i, e.target.value)}>
                  <option value="">Do not import</option>
                  {targets.map((x) => <option key={`${x.entity}.${x.field}`} value={`${x.entity}.${x.field}`}>{entitiesForSheet(file.entity).length > 1 ? `${ENTITY_MAP[x.entity].label}: ` : ""}{x.label}{x.required ? " *" : ""}</option>)}
                </select>
              </div>
            );
          })}
          {!check.ok ? (
            <div className={s.formErr} role="alert" style={{ marginTop: 12 }}>
              {check.missingRequired.length > 0 ? <div>Still to match (required): <b>{check.missingRequired.map(label).join(", ")}</b></div> : null}
              {check.duplicateTargets.length > 0 ? <div>Chosen for two columns: <b>{check.duplicateTargets.map(label).join(", ")}</b></div> : null}
            </div>
          ) : null}
          <StepError error={save.error} onReload={onReload} />
          <div className={s.bar}>
            <span className={s.hint}>* required</span>
            <Button variant="primary" busy={save.isPending} disabled={!check.ok} onClick={() => save.mutate()}>Save columns</Button>
          </div>
        </>
      ) : null}
    </Card>
  );
}
